import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, FileArchive, X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import type { FieldPhoto, MdcpPhotoPoint } from "../types";

export interface LightboxPhoto {
  id: string;
  url: string | null;
  /** Podpis nad zdjęciem, np. „Punkt #3 · zdjęcie 2/4". */
  caption: string;
  /** Nazwa pliku przy pobraniu pojedynczego zdjęcia, np. PUNKT_3__ZDJ_2.jpg. */
  downloadName: string;
}

/** Spłaszcza punkty MDCP do płaskiej listy zdjęć dla lightboxa — kolejność i nazwy
 *  (PUNKT_<nr>__ZDJ_<i>) identyczne jak w backendowym ZIP-ie, żeby podgląd i pobranie
 *  mówiły tym samym językiem. Zdjęcia ODRZUCONE przy odbiorze zostają w podglądzie (widać,
 *  co i dlaczego odpadło), ale numeracji przyjętych nie przesuwają — ZIP ich nie zawiera,
 *  więc własne nazwy dostają osobną serię PUNKT_<nr>__ODRZUCONE_<i>. */
export function buildMdcpLightbox(points: MdcpPhotoPoint[]): LightboxPhoto[] {
  const out: LightboxPhoto[] = [];
  for (const p of points) {
    const accepted = p.photos.filter((ph) => !ph.is_rejected).length;
    let okIdx = 0;
    let rejIdx = 0;
    for (const ph of p.photos) {
      const name = ph.original_name || "";
      const ext = name.includes(".") ? `.${name.split(".").pop()}` : ".jpg";
      if (ph.is_rejected) {
        rejIdx += 1;
        out.push({
          id: ph.id,
          url: ph.file?.url ?? null,
          caption:
            `Punkt #${p.number} · ODRZUCONE ${rejIdx}` +
            (ph.rejection_reason ? ` — ${ph.rejection_reason}` : ""),
          downloadName: `PUNKT_${p.number}__ODRZUCONE_${rejIdx}${ext}`,
        });
      } else {
        okIdx += 1;
        out.push({
          id: ph.id,
          url: ph.file?.url ?? null,
          caption: `Punkt #${p.number} · zdjęcie ${okIdx}/${accepted}`,
          downloadName: `PUNKT_${p.number}__ZDJ_${okIdx}${ext}`,
        });
      }
    }
  }
  return out;
}

/** To samo dla geotagowanych plików wynikowych zamówień (druga warstwa „Zdjęć z terenu").
 *  Ich pozycja bierze się z EXIF-u, nie z punktu, więc podpis mówi, z którego zamówienia
 *  i pozycji zdjęcie pochodzi oraz jak daleko padło od zleconego zakresu — czyli dokładnie
 *  to, po co ta warstwa jest. Nazwa pliku zostaje oryginalna: to luźny plik, nie kadr punktu. */
export function buildFieldPhotoLightbox(photos: FieldPhoto[]): LightboxPhoto[] {
  return photos.map((ph) => {
    const parts = [ph.order_number, ph.line_item_name].filter(Boolean);
    if (ph.nearest) {
      const what = ph.nearest.kind === "parcel" ? "działki" : "obiektu";
      parts.push(`${Math.round(ph.nearest.distance_m)} m od ${what} ${ph.nearest.label}`);
    }
    if (ph.status === "rejected") parts.push("ODRZUCONE");
    else if (ph.status === "pending") parts.push("oczekuje");
    return {
      id: ph.id,
      url: ph.file_url,
      caption: parts.join(" · "),
      downloadName: ph.original_name || "zdjecie.jpg",
    };
  });
}

/** Pełnoekranowy podgląd zdjęć MDCP (zamiast otwierania PNG w nowej karcie):
 *  duży obraz + nawigacja prev/next (strzałki, klawiatura), pobranie pojedynczego
 *  zdjęcia oraz — opcjonalnie — wszystkich naraz jako ZIP. */
export function MdcpPhotoLightbox({
  photos,
  index,
  onIndexChange,
  onClose,
  onDownloadZip,
  zipPending,
}: {
  photos: LightboxPhoto[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  onDownloadZip?: () => void;
  zipPending?: boolean;
}) {
  const count = photos.length;
  const current = photos[index];

  const go = useCallback(
    (delta: number) => onIndexChange((index + delta + count) % count),
    [index, count, onIndexChange],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [go, onClose]);

  if (!current) return null;

  const downloadOne = () => {
    if (!current.url) return;
    const a = document.createElement("a");
    a.href = current.url;
    a.download = current.downloadName;
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return createPortal(
    <div
      data-modal-layer
      className="fixed inset-0 z-[60] flex flex-col bg-black/85"
      onClick={onClose}
    >
      {/* Górny pasek: podpis + akcje (pobierz pojedyncze / ZIP / zamknij). */}
      <div
        className="flex items-center gap-3 px-4 py-2.5 text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm font-mono truncate">{current.caption}</span>
        <span className="ml-auto" />
        <button
          type="button"
          onClick={downloadOne}
          disabled={!current.url}
          title="Pobierz to zdjęcie"
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-white/30 hover:bg-white/10 disabled:opacity-40"
        >
          <Download size={14} /> Pobierz
        </button>
        {onDownloadZip && (
          <button
            type="button"
            onClick={onDownloadZip}
            disabled={zipPending}
            title="Pobierz wszystkie zdjęcia jako ZIP"
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-white/30 hover:bg-white/10 disabled:opacity-60"
          >
            {zipPending ? <Spinner /> : <FileArchive size={14} />} Wszystkie (ZIP)
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Zamknij (Esc)"
          className="p-1 text-white/80 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Obraz + strzałki nawigacji. */}
      <div className="relative flex-1 flex items-center justify-center min-h-0 px-4 pb-4">
        {count > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            title="Poprzednie (←)"
            className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/30 rounded-full"
          >
            <ChevronLeft size={28} />
          </button>
        )}
        {current.url ? (
          <img
            src={current.url}
            alt={current.caption}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <p className="text-white/70 text-sm" onClick={(e) => e.stopPropagation()}>
            Brak podglądu tego pliku.
          </p>
        )}
        {count > 1 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            title="Następne (→)"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/30 rounded-full"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="pb-3 text-center text-xs text-white/60" onClick={(e) => e.stopPropagation()}>
          {index + 1} / {count}
        </div>
      )}
    </div>,
    document.body,
  );
}
